import {forwardRef} from 'react';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {object} from 'yup';

import {Button, Form} from '#component/base';
import {PasswordInputField} from '#component/partial';
import {handleValidationError, passwordValidator} from '#util/validator';
import {ValidationError} from '#util/validator/handleValidationError';

import {FieldValues, ResetPasswordFormProps} from './ResetPasswordForm.types';

export const ResetPasswordForm = forwardRef<HTMLFormElement, ResetPasswordFormProps>(
  ({className, onSubmit, ...otherProps}, ref) => {
    const {
      clearErrors,
      formState: {errors, isSubmitting},
      handleSubmit,
      register,
      setError,
    } = useForm({
      defaultValues: {form: '', password: ''},
      mode: 'onBlur',
      resolver: yupResolver(object({password: passwordValidator})),
    });

    const handleResetPassword = async (fieldValues: FieldValues) => {
      try {
        await onSubmit?.(fieldValues);
      } catch (error) {
        handleValidationError({
          error: error as unknown as ValidationError<FieldValues>,
          errorMessages: {form: 'Unable to reset your password. Try requesting a new link.'},
          setError,
        });
      }
    };

    return (
      <Form
        className={className}
        clearErrors={clearErrors}
        errors={errors}
        onSubmit={onSubmit ? handleSubmit(handleResetPassword) : undefined}
        ref={ref}
        {...otherProps}>
        <PasswordInputField {...register('password')} errors={errors} label="New password" />

        <Button disabled={isSubmitting} icon="ArrowRight" type="submit">
          Confirm
        </Button>
      </Form>
    );
  },
);
